import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useGetOrganisationTierAccessSummaryQuery } from '../services/nestjs-base-api';
import {
  ITierAccessSummary,
  IRestrictedAllowedElements,
  IBenchmarkingAccessResult,
} from '../services/nestjs-base-api';

interface TierAccessState {
  // Loading state
  isLoading: boolean;
  error: any;

  // Element access
  hasUnrestrictedElementAccess: boolean;
  allowedElements: IRestrictedAllowedElements | null;
  isElementAllowed: (elementName: string, type: 'latent' | 'questionCategory') => boolean;

  // Benchmarking access
  benchmarkingAccess: IBenchmarkingAccessResult;
  hasBenchmarkingOverall: boolean;
  hasBenchmarkingProductType: boolean;
  hasBenchmarkingAllLevels: boolean;

  // AI Experts access
  hasAiExpertsAccess: boolean;
  aiCreditsLimit: number;
  aiCreditsUnlimited: boolean;

  // Customer segmentation
  segmentationFieldsLimit: number;
  segmentationUnlimited: boolean;
  hasSegmentationAccess: boolean;

  // Raw data
  tierSummary: ITierAccessSummary | null;
}

/**
 * Hook for accessing subscription tier features
 *
 * This hook provides convenient access to the organization's subscription tier
 * features including element restrictions, benchmarking access levels,
 * AI credits, and customer segmentation limits.
 *
 * @example
 * const { hasUnrestrictedElementAccess, isElementAllowed } = useTierAccess();
 *
 * if (!isElementAllowed('Brand Association', 'latent')) {
 *   return <LockedElement />;
 * }
 */
export function useTierAccess(): TierAccessState {
  const { user } = useAuth();
  const organisationId = user?.organisation?.id;

  const { data, isLoading, error } = useGetOrganisationTierAccessSummaryQuery(
    {
      jwtToken: user?.jwtToken || '',
      orgId: organisationId?.toString() || '',
    },
    {
      skip: !user?.jwtToken || !organisationId,
    }
  );

  const tierAccessState = useMemo<TierAccessState>(() => {
    const defaultBenchmarking: IBenchmarkingAccessResult = {
      overall: false,
      productType: false,
      allLevels: false,
    };

    const defaultAllowedElements: IRestrictedAllowedElements = {
      latentElements: [],
      questionCategories: [],
    };

    // If no data, return default restricted state
    if (!data) {
      return {
        isLoading,
        error,
        hasUnrestrictedElementAccess: false,
        allowedElements: null,
        isElementAllowed: () => false,
        benchmarkingAccess: defaultBenchmarking,
        hasBenchmarkingOverall: false,
        hasBenchmarkingProductType: false,
        hasBenchmarkingAllLevels: false,
        hasAiExpertsAccess: false,
        aiCreditsLimit: 0,
        aiCreditsUnlimited: false,
        segmentationFieldsLimit: 0,
        segmentationUnlimited: false,
        hasSegmentationAccess: false,
        tierSummary: null,
      };
    }

    const hasUnrestrictedElementAccess = data.elementAccess.unrestricted;
    const allowedElements = data.elementAccess.allowedElements || null;

    // Function to check if a specific element is allowed
    const isElementAllowed = (
      elementName: string,
      type: 'latent' | 'questionCategory'
    ): boolean => {
      // If unrestricted, all elements are allowed
      if (hasUnrestrictedElementAccess) {
        return true;
      }

      // If no allowed elements defined, nothing is allowed
      if (!allowedElements) {
        return false;
      }

      // Check against the allowed list
      if (type === 'latent') {
        return allowedElements.latentElements.includes(elementName);
      } else {
        return allowedElements.questionCategories.includes(elementName);
      }
    };

    return {
      isLoading,
      error,

      // Element access
      hasUnrestrictedElementAccess,
      allowedElements,
      isElementAllowed,

      // Benchmarking
      benchmarkingAccess: data.benchmarking,
      hasBenchmarkingOverall: data.benchmarking.overall,
      hasBenchmarkingProductType: data.benchmarking.productType,
      hasBenchmarkingAllLevels: data.benchmarking.allLevels,

      // AI Experts
      hasAiExpertsAccess: data.aiExperts.hasAccess,
      aiCreditsLimit: data.aiExperts.creditsLimit,
      aiCreditsUnlimited: data.aiExperts.unlimited,

      // Segmentation
      segmentationFieldsLimit: data.segmentation.limit,
      segmentationUnlimited: data.segmentation.unlimited,
      hasSegmentationAccess: data.segmentation.limit > 0 || data.segmentation.unlimited,

      // Raw data
      tierSummary: data,
    };
  }, [data, isLoading, error]);

  return tierAccessState;
}

/**
 * Convenience hook specifically for element access
 */
export function useElementAccess() {
  const {
    hasUnrestrictedElementAccess,
    allowedElements,
    isElementAllowed,
    isLoading,
  } = useTierAccess();

  return {
    hasUnrestrictedAccess: hasUnrestrictedElementAccess,
    allowedElements,
    isElementAllowed,
    isLoading,
  };
}

/**
 * Convenience hook specifically for benchmarking access
 */
export function useBenchmarkingTierAccess() {
  const {
    benchmarkingAccess,
    hasBenchmarkingOverall,
    hasBenchmarkingProductType,
    hasBenchmarkingAllLevels,
    isLoading,
  } = useTierAccess();

  return {
    access: benchmarkingAccess,
    hasOverall: hasBenchmarkingOverall,
    hasProductType: hasBenchmarkingProductType,
    hasAllLevels: hasBenchmarkingAllLevels,
    isLoading,
  };
}

/**
 * Convenience hook specifically for AI features
 */
export function useAiTierAccess() {
  const {
    hasAiExpertsAccess,
    aiCreditsLimit,
    aiCreditsUnlimited,
    isLoading,
  } = useTierAccess();

  return {
    hasAccess: hasAiExpertsAccess,
    creditsLimit: aiCreditsLimit,
    unlimited: aiCreditsUnlimited,
    isLoading,
  };
}

/**
 * Convenience hook specifically for segmentation features
 */
export function useSegmentationTierAccess() {
  const {
    segmentationFieldsLimit,
    segmentationUnlimited,
    hasSegmentationAccess,
    isLoading,
  } = useTierAccess();

  return {
    fieldsLimit: segmentationFieldsLimit,
    unlimited: segmentationUnlimited,
    hasAccess: hasSegmentationAccess,
    isLoading,
  };
}

export default useTierAccess;
